"use client"

import { useEffect, useRef } from "react"
import { usePathname } from "next/navigation"
import { signOut, useSession } from "next-auth/react"
import posthog from "posthog-js"
import {
  isAnalyticsPathExcluded,
  STAFF_ANALYTICS_MARKER,
} from "@/lib/analytics/cookies"

const AHREFS_SCRIPT_ID = "ahrefs-public-analytics"
const AHREFS_KEY = "zqYcdLPXodcIta3HxLou+Q"

function hasBrowserPrivacyOptOut() {
  const navigatorWithPrivacy = navigator as Navigator & {
    globalPrivacyControl?: boolean
  }
  return (
    navigator.doNotTrack === "1" ||
    navigatorWithPrivacy.globalPrivacyControl === true
  )
}

export function VisitorTracker() {
  const pathname = usePathname()
  const { data: session, status } = useSession()
  const identifiedUserId = useRef<string | null>(null)
  const enforcingBan = useRef(false)

  useEffect(() => {
    if (status === "loading") return
    if (hasBrowserPrivacyOptOut()) {
      posthog.opt_out_capturing()
      return
    }
    if (session?.user?.isBanned && !enforcingBan.current) {
      enforcingBan.current = true
      void signOut({ callbackUrl: "/login?restricted=1" })
      return
    }
    const isStaff =
      session?.user?.role === "admin" || session?.user?.role === "owner"
    if (isStaff) {
      document.getElementById(AHREFS_SCRIPT_ID)?.remove()
      localStorage.setItem(STAFF_ANALYTICS_MARKER, "1")
      posthog.reset()
      posthog.opt_out_capturing()
      identifiedUserId.current = null
      return
    }

    if (localStorage.getItem(STAFF_ANALYTICS_MARKER) === "1") {
      localStorage.removeItem(STAFF_ANALYTICS_MARKER)
      posthog.opt_in_capturing()
    }

    const userId =
      session?.user && !session.user.isBanned ? session.user.id : null

    if (userId && identifiedUserId.current !== userId) {
      posthog.identify(userId, {
        account_role: session?.user?.role || "user",
      })
      identifiedUserId.current = userId
    } else if (!userId && identifiedUserId.current) {
      posthog.reset()
      identifiedUserId.current = null
    }
  }, [session, status])

  useEffect(() => {
    const excluded = isAnalyticsPathExcluded(pathname)
    const existing = document.getElementById(AHREFS_SCRIPT_ID)
    const privacyOptOut = hasBrowserPrivacyOptOut()
    const isStaff =
      session?.user?.role === "admin" || session?.user?.role === "owner"

    if (status === "loading" || excluded || privacyOptOut || isStaff) {
      existing?.remove()
      return
    }

    if (!existing) {
      const script = document.createElement("script")
      script.id = AHREFS_SCRIPT_ID
      script.src = "https://analytics.ahrefs.com/analytics.js"
      script.dataset.key = AHREFS_KEY
      script.async = true
      document.head.appendChild(script)
    }

    const currentUrl = `${window.location.origin}${pathname}${window.location.search}`
    const routeParts = pathname.split("/").filter(Boolean)
    const routeContext = {
      path: pathname,
      route_section: routeParts[0] || "home",
      game_category:
        routeParts[0] === "play" ? routeParts[1] || null : null,
      game_slug:
        routeParts[0] === "play" ? routeParts[2] || null : null,
      environment:
        window.location.hostname.startsWith("beta.") ? "staging" : "production",
    }

    posthog.register({
      product: "higher-lower",
      environment: routeContext.environment,
    })
    posthog.capture("$pageview", {
      $current_url: currentUrl,
      ...routeContext,
    })

    void fetch("/api/analytics/track", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({
        type: "pageview",
        path: pathname,
        title: document.title,
        referrer: document.referrer,
      }),
      keepalive: true,
    })

    const enteredAt = Date.now()
    return () => {
      posthog.capture("page_exited", {
        ...routeContext,
        dwell_seconds: Math.max(
          0,
          Math.round((Date.now() - enteredAt) / 100) / 10,
        ),
      })
    }
  }, [pathname, session?.user?.role, status])

  return null
}
